/**
 * bulkSends.js — Pinia store for the native bulk-send UI (v2026.05 flat-attempts).
 *
 * Three surfaces, one store:
 *  - List page: paginated `list` + `loadList(params)`
 *  - Detail page: `current` (one batch) + `loadDetail(jobGuid)` with ETag polling
 *  - Wizard: `pendingSubmit` flag + `submit(templateGuid, payload, key)`
 *
 * **Flat-attempts model (v2026.05 refactor):** the previous tree/chain model
 * was retired. Plan:
 * `C:\Users\Helios\.claude\plans\bulk-send-flat-attempts-refactor.md`.
 *
 * The detail-page response now embeds the per-recipient merged view
 * server-side as `job.recipients[]`, with `attempts[]` per recipient
 * (chronological) and a per-batch `cycles[]` summary. The chain-walking
 * code, `retryChain`/`recipientChain` getters, `loadChain`,
 * `refreshInFlightChainMembers`, and the `MAX_CHAIN_FETCHES` cap are all
 * GONE — server delivers the merged shape in one round-trip.
 *
 * Channel parity (plan §2): the store never reshapes a server response.
 * Every field rendered in any view comes from Platform.API JSON verbatim.
 * `displayName` in particular is server-formatted.
 *
 * Polling design (plan §5.1): the detail page calls `loadDetail` every 5s
 * with the previously-received ETag. The server replies 304 when nothing
 * changed; the store records the activity (so the connection-lost banner
 * stays hidden) and returns without merging. On a real 200, the new payload
 * replaces `current` and a fresh ETag is stashed. The view owns the polling
 * loop so unmount cancels cleanly — the store is polling-agnostic.
 */
import { defineStore } from 'pinia';
import signApi from '@services/signApi.js';

const TERMINAL_STATUSES = new Set(['Completed', 'Failed', 'PartiallyCompleted']);

export const useBulkSendsStore = defineStore('bulkSends', {
    state: () => ({
        // ---- List page ----
        list: [],
        listTotal: 0,
        listLoading: false,
        listError: null,

        // ---- Detail page ----
        // BulkSendJobStatusProxyResponse | null. Contains job-level metadata
        // (status / counters / activeCycleId), a cycles[] summary, and a
        // recipients[] array where each recipient has its own attempts[] —
        // all server-merged. The view consumes `current.recipients` directly.
        current: null,
        currentEtag: null,
        detailLoading: false,
        lastDetailError: null,
        lastDetailFetchAt: null,

        // ---- Wizard ----
        pendingSubmit: false,
        submitError: null
    }),

    getters: {
        /**
         * True iff the current batch is in a terminal state. The detail
         * view stops the polling loop on this transition.
         * @returns {boolean}
         */
        isCurrentTerminal: (state) => {
            if (!state.current) return false;
            return TERMINAL_STATUSES.has(state.current.status);
        },

        /**
         * True iff a retry-failed click is permitted on the current batch.
         * Mirrors the server-side gate (batch terminal AND no in-flight cycle).
         * @returns {boolean}
         */
        canRetryFailed: (state) => {
            if (!state.current) return false;
            return TERMINAL_STATUSES.has(state.current.status)
                && !state.current.activeCycleId
                && (state.current.counters?.failed ?? 0) > 0;
        }
    },

    actions: {
        /**
         * Load the bulk-sends list. Channel-parity: the response is the
         * same paginated page Platform.API serves to every channel — the
         * portal does NOT post-process, sort, or augment.
         * @param {{page?: number, pageSize?: number, status?: string}} [params]
         */
        async loadList(params = {}) {
            this.listLoading = true;
            this.listError = null;
            try {
                const resp = await signApi.bulkSends.list(params);
                const data = resp.data || {};
                this.list = data.items || [];
                this.listTotal = data.totalCount || 0;
            } catch (err) {
                this.listError =
                    err.response?.data?.message
                    || err.response?.data?.errorMessage
                    || err.displayMessage
                    || 'Failed to load bulk sends';
                this.list = [];
                this.listTotal = 0;
            } finally {
                this.listLoading = false;
            }
        },

        /**
         * Fetch the latest snapshot of a single batch. Honors the
         * previously-stashed ETag so a 304 is a cheap no-op.
         * @param {string} jobGuid
         */
        async loadDetail(jobGuid) {
            this.detailLoading = true;
            this.lastDetailError = null;
            try {
                const ifNoneMatch = this.currentEtag || undefined;
                const resp = await signApi.bulkSends.get(jobGuid, ifNoneMatch);
                this.lastDetailFetchAt = Date.now();

                if (resp.status === 304) {
                    // No change. Keep current + etag as-is.
                    return this.current;
                }

                this.current = resp.data || null;
                const newEtag = resp.headers?.etag || resp.headers?.ETag || null;
                if (newEtag) this.currentEtag = newEtag;
                return this.current;
            } catch (err) {
                this.lastDetailError =
                    err.response?.data?.message
                    || err.response?.data?.errorMessage
                    || err.displayMessage
                    || 'Failed to load bulk-send job';
                throw err;
            } finally {
                this.detailLoading = false;
            }
        },

        /**
         * Reset detail-page state. Call from the view's `beforeUnmount` so
         * navigating between two different jobGuids doesn't leak the prior
         * ETag (which would 304 forever on the new job).
         */
        clearDetail() {
            this.current = null;
            this.currentEtag = null;
            this.lastDetailError = null;
            this.lastDetailFetchAt = null;
        },

        /**
         * Submit a new bulk-send run. The idempotency key is minted by the
         * wizard at entry (crypto.randomUUID) so a network blip during
         * submit retries against the same key and the server returns the
         * original jobGuid — never duplicating the run.
         *
         * @param {string} templateGuid
         * @param {{recipients: Array}} payload
         * @param {string} idempotencyKey
         * @returns {Promise<{jobGuid: string, totalRecipients: number, statusUrl: string}>}
         */
        async submit(templateGuid, payload, idempotencyKey) {
            this.pendingSubmit = true;
            this.submitError = null;
            try {
                const resp = await signApi.bulkSends.submit(templateGuid, payload, idempotencyKey);
                return resp.data;
            } catch (err) {
                this.submitError =
                    err.response?.data?.message
                    || err.response?.data?.errorMessage
                    || err.displayMessage
                    || 'Failed to submit bulk send';
                throw err;
            } finally {
                this.pendingSubmit = false;
            }
        },

        /**
         * Retry the failed recipients of a terminal batch as a new CYCLE on
         * the same batch. Same jobGuid stays on the URL; the new cycleId +
         * cycleNumber identify the cycle within `current.cycles`.
         *
         * Returns `{ jobGuid, cycleId, cycleNumber, retriedRecipientCount,
         * skippedTerminalCount, skippedTerminalDetails, statusUrl }`.
         *
         * @param {string} jobGuid
         */
        async retryFailed(jobGuid) {
            try {
                const resp = await signApi.bulkSends.retryFailed(jobGuid);
                return resp.data;
            } catch (err) {
                // Typed error pass-through so the view can render the
                // 429-with-Retry-After dialog distinctly from a generic
                // failure.
                const code = err.response?.data?.errorCode || err.response?.data?.error;
                const retryAfter = err.response?.headers?.['retry-after'];
                err.bulkSendCode = code;
                err.retryAfterSeconds = retryAfter ? parseInt(retryAfter, 10) : null;
                throw err;
            }
        }
    }
});
